import { Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faShieldExclamation,
    faUsers,
    faFlag,
    faArrowUp,
    faArrowDown,
    faCircleExclamation
} from '@fortawesome/pro-light-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Découpe le body d'impact en sections H3
const splitH3 = (md: string) => {
    const chunks = (md || '').split(/\n(?=### )/);
    const intro = chunks[0].startsWith('### ') ? '' : (chunks.shift() || '');
    return {
        intro: intro.trim(),
        sections: chunks.map((c) => {
            const nl = c.indexOf('\n');
            const title = c.slice(4, nl > 0 ? nl : undefined).trim();
            const body = nl > 0 ? c.slice(nl + 1).trim() : '';
            return { title, body };
        })
    };
};

// Détermine l'icône et la couleur selon le titre de section
const sectionMeta = (title: string): { icon: any; color: string; bg: string } => {
    const t = title.toLowerCase();
    if (t.includes('enjeu') || t.includes('indicateur') || t.includes('stake') || t.includes('kpi')) {
        return { icon: faChartLine, color: '#1677ff', bg: 'rgba(22,119,255,0.06)' };
    }
    if (t.includes('risque') || t.includes('risk') || t.includes('opportunit')) {
        return { icon: faShieldExclamation, color: '#d4380d', bg: 'rgba(212,56,13,0.05)' };
    }
    if (t.includes('partie') || t.includes('stakeholder') || t.includes('acteur')) {
        return { icon: faUsers, color: '#531dab', bg: 'rgba(83,29,171,0.05)' };
    }
    if (t.includes('priorit') || t.includes('action') || t.includes('suggér')) {
        return { icon: faFlag, color: '#d48806', bg: 'rgba(212,136,6,0.05)' };
    }
    return { icon: faCircleExclamation, color: '#595959', bg: 'transparent' };
};

// Extrait "MONTE" / "DESCEND" du titre d'un item pour afficher un badge
const extractDirection = (bold: string): 'up' | 'down' | null => {
    if (/monte|↑|hausse|augmente/i.test(bold)) return 'up';
    if (/descend|↓|baisse|diminue/i.test(bold)) return 'down';
    return null;
};

// Nettoie un titre bold des mentions MONTE/DESCEND
const cleanBoldTitle = (s: string) =>
    s.replace(/\s*[—–-]\s*(MONTE|DESCEND|↑|↓|hausse|baisse|augmente|diminue)[^)"]*/gi, '').trim();

// Splitpe un body de section en items (paragraphes avec titre bold)
const parseItems = (body: string): Array<{ title: string; text: string; direction: 'up' | 'down' | null }> => {
    // Chaque item commence par **Titre** au début d'un paragraphe
    const blocks = body.split(/\n\n+/).filter(Boolean);
    const items: Array<{ title: string; text: string; direction: 'up' | 'down' | null }> = [];

    for (const block of blocks) {
        const boldMatch = block.match(/^\*\*([^*]+)\*\*/);
        if (boldMatch) {
            const rawTitle = boldMatch[1];
            const direction = extractDirection(rawTitle);
            const title = cleanBoldTitle(rawTitle);
            // Reste du paragraphe après le titre bold
            const text = block.slice(boldMatch[0].length).replace(/^\s*[—–-]\s*/, '').trim();
            items.push({ title, text, direction });
        } else {
            // Paragraphe sans titre bold (ex: liste numérotée, texte libre)
            items.push({ title: '', text: block.trim(), direction: null });
        }
    }
    return items;
};

const DirectionBadge = ({ direction }: { direction: 'up' | 'down' }) => (
    <Tag
        color={direction === 'up' ? 'red' : 'green'}
        style={{ marginLeft: 8, fontSize: 11, padding: '0 6px', verticalAlign: 'middle' }}
    >
        <FontAwesomeIcon icon={(direction === 'up' ? faArrowUp : faArrowDown) as any} style={{ marginRight: 4 }} />
        {direction === 'up' ? 'Monte' : 'Descend'}
    </Tag>
);

const ItemCard = ({ title, text, direction, accentColor }: {
    title: string; text: string; direction: 'up' | 'down' | null; accentColor: string;
}) => (
    <div style={{
        borderLeft: `3px solid ${accentColor}`,
        paddingLeft: 12,
        marginBottom: 12,
    }}>
        {title && (
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, color: 'inherit' }}>
                {title}
                {direction && <DirectionBadge direction={direction} />}
            </div>
        )}
        <div style={{ fontSize: 13, color: '#595959', lineHeight: 1.6 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
        </div>
    </div>
);

export const WatchImpactSection = ({ body }: { body: string }) => {
    const { intro, sections } = splitH3(body);

    return (
        <div className="watch-impact-section">
            {intro && (
                <p style={{ fontStyle: 'italic', color: '#595959', marginBottom: 20, fontSize: 13 }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{intro}</ReactMarkdown>
                </p>
            )}
            {sections.map((sec, i) => {
                const { icon, color, bg } = sectionMeta(sec.title);
                const items = parseItems(sec.body);
                return (
                    <div key={i} style={{
                        background: bg,
                        borderRadius: 8,
                        padding: '14px 16px',
                        marginBottom: 16,
                        border: `1px solid ${color}22`
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontWeight: 600,
                            fontSize: 13,
                            color,
                            marginBottom: 14,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5
                        }}>
                            <FontAwesomeIcon icon={icon as any} />
                            {sec.title}
                        </div>
                        {items.map((item, j) => (
                            <ItemCard
                                key={j}
                                title={item.title}
                                text={item.text}
                                direction={item.direction}
                                accentColor={color}
                            />
                        ))}
                    </div>
                );
            })}
        </div>
    );
};
